// Proficiência de Armas (Tibia 15, igual ao Global): cada arma ganha experiência de proficiência com os abates e,
// a cada nível liberado, o jogador escolhe 1 dos bônus daquele nível. Depois do último nível, 2 níveis de maestria
// (prata e ouro). Dados oficiais: proficiencies.json do Canary (árvores) e appearances.dat (arma -> árvore).
// Tabelas de experiência, experiência por abate (estrelas do bestiário e categoria do bosstiário) e tipos de bônus: Canary.

export interface ProfPerk {
  /** tipo (WeaponProficiencyBonus_t do Canary; 99 = míssil teleguiado das Moonsilver) */
  t: number; v: number;
  sk?: string; el?: string; aug?: number; sp?: string; r?: number; be?: string;
}
export interface ProfTree { name: string; levels: ProfPerk[][]; wiki?: boolean }
/** estado do jogador numa arma: experiência e o bônus escolhido em cada nível (-1 = nenhum) */
export interface ProfState { xp: number; perks: number[] }

export const PROF_XP = {
  standard: [1750, 25000, 100000, 400000, 2000000, 8000000, 30000000, 60000000, 90000000],
  knight: [1250, 20000, 80000, 300000, 1500000, 6000000, 20000000, 40000000, 60000000],
  crossbow: [600, 8000, 30000, 150000, 650000, 2500000, 10000000, 20000000, 30000000],
};
/** tabela de experiência da arma (Canary: besta; armas de knight; o resto) */
export function profTable(t: { name: string; weaponType?: string; vocations?: string[]; ammoType?: string }): number[] {
  if (t.ammoType === 'bolt') return PROF_XP.crossbow;
  if ((t.weaponType === 'sword' || t.weaponType === 'axe' || t.weaponType === 'club') && (!t.vocations || t.vocations.includes('knight'))) return PROF_XP.knight;
  return PROF_XP.standard;
}
/** experiência máxima: último nível + 2 de maestria */
export function profMaxXp(tree: ProfTree, table: number[]) { return table[Math.min(table.length, tree.levels.length + 2) - 1] ?? 0; }
/** níveis liberados pela experiência */
export function profUnlocked(tree: ProfTree, table: number[], xp: number) {
  let n = 0; for (let i = 0; i < Math.min(table.length, tree.levels.length); i++) { if (xp < table[i]!) break; n++; }
  return n;
}
/** nível de maestria mostrado (0..7) e se está em prata (1º nível extra) ou ouro (2º, maestria completa) */
export function profMastery(tree: ProfTree, table: number[], xp: number): { level: number; tier: '' | 'silver' | 'gold' } {
  const lv = profUnlocked(tree, table, xp), L = tree.levels.length;
  if (lv < L) return { level: lv, tier: '' };
  if (xp >= (table[L + 1] ?? Infinity)) return { level: lv, tier: 'gold' };
  if (xp >= (table[L] ?? Infinity)) return { level: lv, tier: 'silver' };
  return { level: lv, tier: '' };
}
/** próxima meta de experiência (ou null se dominada) */
export function profNext(tree: ProfTree, table: number[], xp: number): number | null {
  for (let i = 0; i < Math.min(table.length, tree.levels.length + 2); i++) if (xp < table[i]!) return table[i]!;
  return null;
}

/** experiência por abate: estrelas do bestiário (Canary: polinômio) e categoria do bosstiário */
export function profKillXp(stars: number, bossCat?: 'bane' | 'archfoe' | 'nemesis'): number {
  const s = Math.max(0, Math.min(5, stars));
  const poly = Math.max(0, Math.floor(-1.133 * s ** 5 + 14.083 * s ** 4 - 59.666 * s ** 3 + 102.916 * s ** 2 - 27.2 * s + 1));
  const boss = bossCat === 'nemesis' ? 15000 : bossCat === 'archfoe' ? 5000 : bossCat === 'bane' ? 500 : 0;
  return poly + boss;
}

const pct = (v: number) => `${(Math.round(v * 10000) / 100).toLocaleString('pt-BR')}%`;
const SK: Record<string, string> = { sword: 'Sword Fighting', axe: 'Axe Fighting', club: 'Club Fighting', distance: 'Distance Fighting', shielding: 'Shielding', fist: 'Fist Fighting', magic: 'Magic Level', fishing: 'Fishing' };
const EL: Record<string, string> = { physical: 'Physical', fire: 'Fire', earth: 'Earth', energy: 'Energy', ice: 'Ice', holy: 'Holy', death: 'Death', healing: 'Healing' };
const AUG: Record<number, string> = { 2: 'dano base', 3: 'cura base', 6: 'recarga', 14: 'life leech', 15: 'mana leech', 16: 'dano crítico extra', 17: 'chance de crítico' };

/** texto do bônus como no cliente do Global (em português) */
export function perkText(p: ProfPerk): string {
  switch (p.t) {
    case 0: return `+${p.v} de ataque`;
    case 1: return `+${p.v} de defesa`;
    case 2: return `+${p.v} no modificador de defesa da arma`;
    case 3: return `+${p.v} de ${SK[p.sk ?? ''] ?? p.sk}`;
    case 4: return `+${p.v} de ${EL[p.el ?? ''] ?? ''} Magic Level`;
    case 5: return p.aug === 6 ? `-${Math.abs(p.v)} s de recarga de ${p.sp}` : `+${pct(p.v)} de ${AUG[p.aug ?? 0] ?? 'bônus'} para ${p.sp}`;
    case 6: return `+${pct(p.v)} de dano contra ${p.be}`;
    case 7: return `+${pct(p.v)} de dano contra bosses e criaturas especiais`;
    case 8: return `+${pct(p.v)} de chance de crítico`;
    case 9: return `+${pct(p.v)} de chance de crítico para magias e runas de ${EL[p.el ?? ''] ?? ''}`;
    case 10: return `+${pct(p.v)} de chance de crítico para runas`;
    case 11: return `+${pct(p.v)} de chance de crítico para ataques automáticos`;
    case 12: return `+${pct(p.v)} de dano crítico extra`;
    case 13: return `+${pct(p.v)} de dano crítico extra para magias e runas de ${EL[p.el ?? ''] ?? ''}`;
    case 14: return `+${pct(p.v)} de dano crítico extra para runas`;
    case 15: return `+${pct(p.v)} de dano crítico extra para ataques automáticos`;
    case 16: return `+${pct(p.v)} de mana leech`;
    case 17: return `+${pct(p.v)} de life leech`;
    case 18: return `+${p.v} de mana a cada acerto`;
    case 19: return `+${p.v} de vida a cada acerto`;
    case 20: return `+${p.v} de mana a cada abate`;
    case 21: return `+${p.v} de vida a cada abate`;
    case 22: return `+${p.v} de dano no tiro perfeito a ${p.r} quadrados`;
    case 23: return `+${pct(p.v)} de chance de acerto à distância`;
    case 24: return `+${p.v} de alcance`;
    case 25: return `+${pct(p.v)} do seu ${SK[p.sk ?? ''] ?? ''} como dano extra nos ataques automáticos`;
    case 26: return `+${pct(p.v)} do seu ${SK[p.sk ?? ''] ?? ''} como dano extra nas magias`;
    case 27: return `+${pct(p.v)} do seu ${SK[p.sk ?? ''] ?? ''} como cura extra nas magias`;
    case 28: return `+${pct(p.v)} de dano contra alvos acima de 95% da vida`;
    case 29: return `+${pct(p.v)} de dano contra alvos abaixo de 30% da vida`;
    case 30: return `+${pct(p.v)} de penetração de armadura`;
    case 31: return `+${pct(p.v)} de penetração de ${EL[p.el ?? ''] ?? ''}`;
    case 99: return `${pct(p.v)} de chance de as magias ofensivas soltarem um míssil teleguiado de ${EL[p.el ?? ''] ?? ''} (dano = 200% do seu level)`;
    default: return 'bônus';
  }
}

/** Bônus somados dos níveis escolhidos de uma arma (usados no combate). */
export interface ProfBonus {
  atk: number; def: number; defMod: number; skills: Record<string, number>;
  /** dano/cura/crítico/leech/recarga por magia (nome) */
  spells: Record<string, { dmg: number; heal: number; cd: number; life: number; mana: number; critC: number; critD: number }>;
  bestiary: Record<string, number>; foe: number;
  crit: { c: number; d: number }; autoCrit: { c: number; d: number }; runeCrit: { c: number; d: number }; elemCrit: Record<string, { c: number; d: number }>;
  life: number; mana: number; lifeHit: number; manaHit: number; lifeKill: number; manaKill: number;
  perfect: { range: number; dmg: number } | null; hit: number; range: number;
  skillAuto: Record<string, number>; skillSpell: Record<string, number>; skillHeal: Record<string, number>;
  alpha: number; omega: number; armorPen: number; pierce: Record<string, number>; homing: { el: string; chance: number } | null;
}
export function emptyProfBonus(): ProfBonus {
  return { atk: 0, def: 0, defMod: 0, skills: {}, spells: {}, bestiary: {}, foe: 0, crit: { c: 0, d: 0 }, autoCrit: { c: 0, d: 0 }, runeCrit: { c: 0, d: 0 }, elemCrit: {},
    life: 0, mana: 0, lifeHit: 0, manaHit: 0, lifeKill: 0, manaKill: 0, perfect: null, hit: 0, range: 0, skillAuto: {}, skillSpell: {}, skillHeal: {},
    alpha: 0, omega: 0, armorPen: 0, pierce: {}, homing: null };
}
export function profBonus(tree: ProfTree | undefined, st: ProfState | undefined, table: number[]): ProfBonus {
  const b = emptyProfBonus();
  if (!tree || !st) return b;
  const unlocked = profUnlocked(tree, table, st.xp);
  const add = (o: Record<string, number>, k: string | undefined, v: number) => { if (k) o[k] = (o[k] ?? 0) + v; };
  for (let lv = 0; lv < unlocked; lv++) {
    const p = tree.levels[lv]?.[st.perks[lv] ?? -1];
    if (!p) continue;
    switch (p.t) {
      case 0: b.atk += p.v; break;
      case 1: b.def += p.v; break;
      case 2: b.defMod += p.v; break;
      case 3: add(b.skills, p.sk, p.v); break;
      case 4: add(b.skills, 'ml' + (p.el ?? ''), p.v); break;
      case 5: {
        const s = (b.spells[p.sp ?? ''] ??= { dmg: 0, heal: 0, cd: 0, life: 0, mana: 0, critC: 0, critD: 0 });
        if (p.aug === 2) s.dmg += p.v; else if (p.aug === 3) s.heal += p.v; else if (p.aug === 6) s.cd += Math.abs(p.v) * 1000;
        else if (p.aug === 14) s.life += p.v; else if (p.aug === 15) s.mana += p.v; else if (p.aug === 16) s.critD += p.v; else if (p.aug === 17) s.critC += p.v;
        break;
      }
      case 6: add(b.bestiary, p.be, p.v); break;
      case 7: b.foe += p.v; break;
      case 8: b.crit.c += p.v; break;
      case 12: b.crit.d += p.v; break;
      case 11: b.autoCrit.c += p.v; break;
      case 15: b.autoCrit.d += p.v; break;
      case 10: b.runeCrit.c += p.v; break;
      case 14: b.runeCrit.d += p.v; break;
      case 9: (b.elemCrit[p.el ?? ''] ??= { c: 0, d: 0 }).c += p.v; break;
      case 13: (b.elemCrit[p.el ?? ''] ??= { c: 0, d: 0 }).d += p.v; break;
      case 16: b.mana += p.v; break;
      case 17: b.life += p.v; break;
      case 18: b.manaHit += p.v; break;
      case 19: b.lifeHit += p.v; break;
      case 20: b.manaKill += p.v; break;
      case 21: b.lifeKill += p.v; break;
      case 22: b.perfect = { range: p.r ?? 0, dmg: (b.perfect?.dmg ?? 0) + p.v }; break;
      case 23: b.hit += p.v; break;
      case 24: b.range += p.v; break;
      case 25: add(b.skillAuto, p.sk, p.v); break;
      case 26: add(b.skillSpell, p.sk, p.v); break;
      case 27: add(b.skillHeal, p.sk, p.v); break;
      case 28: b.alpha += p.v; break;
      case 29: b.omega += p.v; break;
      case 30: b.armorPen += p.v; break;
      case 31: add(b.pierce, p.el, p.v); break;
      case 99: b.homing = { el: p.el ?? 'physical', chance: (b.homing?.chance ?? 0) + p.v }; break;
    }
  }
  return b;
}

/** ícone do bônus nas folhas oficiais do cliente (assets/interface/images/proficiency) */
const MAIN_IDX: Record<number, number> = { 0: 0, 1: 1, 2: 2, 7: 3, 8: 4, 10: 5, 11: 6, 12: 7, 14: 8, 15: 9, 17: 10, 16: 11, 19: 12, 18: 13, 21: 14, 20: 15, 22: 16, 23: 17, 24: 18, 28: 19, 29: 20, 30: 21 };
const SKILL_IDX: Record<string, number> = { sword: 0, axe: 1, club: 2, fist: 3, magic: 4, shielding: 5, distance: 6, fishing: 7 };
const ELEM_IDX: Record<string, number> = { physical: 0, fire: 1, energy: 2, earth: 3, ice: 4, death: 5, holy: 6, healing: 6 };
export const BESTIARY_CLASSES = ['Amphibic', 'Aquatic', 'Bird', 'Construct', 'Demon', 'Dragon', 'Elemental', 'Extra Dimensional', 'Fey', 'Giant', 'Human', 'Humanoid',
  'Inkborn', 'Lycanthrope', 'Magical', 'Mammal', 'Plant', 'Reptile', 'Slime', 'Undead', 'Vermin'];
export function perkIcon(p: ProfPerk): { sheet: string; index: number } | { spell: string } {
  const D = '/assets/interface/images/proficiency/';
  if (p.t === 5) return { spell: p.sp ?? '' };
  if (p.t === 3) return { sheet: D + 'icons-weaponmastery-offensiveBonusSkill.png', index: SKILL_IDX[p.sk ?? ''] ?? 0 };
  if (p.t === 25) return { sheet: D + 'icons-weaponmastery-gainExtraDamageAutoAttack.png', index: SKILL_IDX[p.sk ?? ''] ?? 0 };
  if (p.t === 26) return { sheet: D + 'icons-weaponmastery-gainExtraDamageSpells.png', index: SKILL_IDX[p.sk ?? ''] ?? 0 };
  if (p.t === 27) return { sheet: D + 'icons-weaponmastery-gainExtraHealingSpells.png', index: SKILL_IDX[p.sk ?? ''] ?? 0 };
  if (p.t === 6) return { sheet: D + 'icons-weaponmastery-damagaAgainstBestiary.png', index: Math.max(0, BESTIARY_CLASSES.indexOf(p.be ?? '')) };
  if (p.t === 4) return { sheet: D + 'icon-weaponsmastery-specializedMagicLevel.png', index: ELEM_IDX[p.el ?? ''] ?? 0 };
  if (p.t === 9) return { sheet: D + 'icons-weaponmastery-criticalHitChanceElement.png', index: ELEM_IDX[p.el ?? ''] ?? 0 };
  if (p.t === 13) return { sheet: D + 'icons-weaponmastery-criticalExtraDamageElement.png', index: ELEM_IDX[p.el ?? ''] ?? 0 };
  if (p.t === 31) return { sheet: D + 'icons-weaponmastery-elementalPiercing.png', index: ELEM_IDX[p.el ?? ''] ?? 0 };
  if (p.t === 99) return { sheet: D + 'icons-weaponmastery-homingMissile.png', index: ELEM_IDX[p.el ?? ''] ?? 0 };
  return { sheet: D + 'icons-weaponmastery.png', index: MAIN_IDX[p.t] ?? 0 };
}
